/*
 * Aykasa matching-lid add-on.
 *
 * The crate pages already advertise "25% off the matching lid", but acting on
 * it meant finding the lid product and adding it yourself. This turns the offer
 * into a tick box directly above Add to cart: the crate and its matching lid go
 * into the cart in one request, so the automatic lid discount shows on the lid
 * line straight away.
 *
 * Canopy's product-form.js posts FormData(this.form) to theme.routes.cartAdd
 * with a `sections` list, then paints the cart icon and drawer from the
 * response. We do the same, so the drawer, the icon count and lazy images
 * behave exactly as they do for a normal add to cart.
 *
 * Untick it and this script stays out of the way: the theme's own handler runs
 * untouched.
 */
(function () {
  'use strict';

  var widget = document.querySelector('[data-ak-lid-addon]');
  if (!widget) return;

  var form =
    document.querySelector('form.js-product-form-main') ||
    document.querySelector('form[action*="/cart/add"]');
  var checkbox = widget.querySelector('input[type="checkbox"]');
  if (!form || !checkbox) return;

  var lidVariantId = widget.getAttribute('data-lid-variant');
  var lidHandle = widget.getAttribute('data-lid-handle');
  if (!lidVariantId || !lidHandle) return;

  var priceWas = widget.querySelector('[data-ak-lid-was]');
  var priceNow = widget.querySelector('[data-ak-lid-now]');
  var photo = widget.querySelector('[data-ak-lid-photo]');
  var errorEl = widget.querySelector('[data-ak-lid-error]');
  var qtyInput = widget.querySelector('[data-ak-lid-qty]');
  var stepButtons = widget.querySelectorAll('[data-ak-lid-step]');
  var addButton = form.querySelector('[name="add"]');
  var busy = false;

  var LID_QTY_MIN = 1;
  var LID_QTY_MAX = 99;

  syncSwitch();
  fillPrices().then(reveal, reveal);

  // Capture phase: our handler runs before Canopy's listener on the same form.
  form.addEventListener('submit', handleSubmit, true);
  checkbox.addEventListener('change', syncSwitch);

  stepButtons.forEach(function (button) {
    button.addEventListener('click', function () {
      var step = parseInt(button.getAttribute('data-ak-lid-step'), 10);
      setLidQty(readLidQty() + (isNaN(step) ? 0 : step));
    });
  });
  if (qtyInput) {
    // Typing a number and leaving the field shouldn't be able to send an
    // out-of-range quantity, so normalise on both events.
    qtyInput.addEventListener('change', function () { setLidQty(readLidQty()); });
    qtyInput.addEventListener('blur', function () { setLidQty(readLidQty()); });
    setLidQty(readLidQty());
  }

  function reveal() {
    widget.hidden = false;
  }

  // The quantity stepper only makes sense once the lid is actually coming.
  function syncSwitch() {
    widget.classList.toggle('is-on', checkbox.checked);
  }

  function readLidQty() {
    return clampQty(parseInt(qtyInput && qtyInput.value, 10));
  }

  function setLidQty(value) {
    if (!qtyInput) return;
    var qty = clampQty(value);
    qtyInput.value = String(qty);
    stepButtons.forEach(function (button) {
      var step = parseInt(button.getAttribute('data-ak-lid-step'), 10);
      button.disabled = step < 0 ? qty <= LID_QTY_MIN : qty >= LID_QTY_MAX;
    });
  }

  function clampQty(value) {
    if (isNaN(value)) return LID_QTY_MIN;
    return Math.min(LID_QTY_MAX, Math.max(LID_QTY_MIN, value));
  }

  // Prices come from the shop rather than being typed into the theme, so a lid
  // price change can't leave the product page quoting a stale number.
  function fillPrices() {
    return fetch('/products/' + lidHandle + '.js')
      .then(function (response) {
        if (!response.ok) throw new Error(response.status);
        return response.json();
      })
      .then(function (product) {
        var variant = (product.variants || []).filter(function (v) {
          return String(v.id) === String(lidVariantId);
        })[0];
        if (!variant) return;
        if (priceWas) priceWas.textContent = money(variant.price);
        // Shopify applies 25% off the line and rounds the net price, which is
        // what Math.round(price * 0.75) reproduces.
        if (priceNow) priceNow.textContent = money(Math.round(variant.price * 0.75));
        showPhoto(variant.featured_image || product.featured_image);
      });
  }

  // The variant carries its own photo, so a shopper sees the exact lid they are
  // adding rather than the product's default shot.
  function showPhoto(image) {
    if (!photo || !image) return;
    var src = image.src || image.url || image;
    if (typeof src !== 'string' || !src) return;
    photo.addEventListener('error', function () {
      photo.hidden = true;
    });
    photo.src = src + (src.indexOf('?') === -1 ? '?' : '&') + 'width=120';
    photo.hidden = false;
  }

  function handleSubmit(event) {
    if (!checkbox.checked || busy) return;

    var idInput = form.querySelector('[name="id"]');
    if (!idInput || !idInput.value) return;

    // Ours, so Canopy's handler must not also run and add the crate twice.
    event.preventDefault();
    event.stopImmediatePropagation();

    var crateQtyInput = form.querySelector('[name="quantity"]');
    var crateQty = Math.max(1, parseInt(crateQtyInput && crateQtyInput.value, 10) || 1);
    var lidQty = readLidQty();
    var crateVariantId = idInput.value;

    var body = new FormData();
    body.append('items[0][id]', crateVariantId);
    body.append('items[0][quantity]', String(crateQty));
    body.append('items[1][id]', lidVariantId);
    body.append('items[1][quantity]', String(lidQty));
    body.append('form_type', 'product');
    body.append('utf8', '\u2713');

    var cartDrawer = document.querySelector('cart-drawer');
    var sections = ['cart-icon-bubble'];
    if (cartDrawer) {
      var drawerSection = cartDrawer.closest('.shopify-section');
      if (drawerSection) {
        sections.push(drawerSection.id.replace('shopify-section-', ''));
      }
    }
    body.append('sections', sections.join(','));
    body.append('sections_url', window.location.pathname);

    clearError();
    setBusy(true);

    fetch(theme.routes.cartAdd, {
      method: 'POST',
      headers: {
        Accept: 'application/javascript',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: body,
    })
      .then(function (response) {
        return response.json().then(function (data) {
          if (!response.ok) {
            throw new Error(data.description || data.message || response.status);
          }
          return data;
        });
      })
      .then(function (data) {
        setBusy(false);
        form.dispatchEvent(
          new CustomEvent('on:cart:add', {
            bubbles: true,
            detail: { variantId: crateVariantId },
          })
        );
        if (theme.settings.afterAtc === 'page') {
          window.location.href = theme.routes.cart;
          return;
        }
        if (window.ProductForm && data.sections) ProductForm.updateCartIcon(data);
        if (cartDrawer && data.sections) {
          cartDrawer.renderContents(data, shouldOpenDrawer());
        } else if (!data.sections) {
          // Without section markup there is nothing trustworthy to repaint, so
          // send the shopper somewhere that shows what actually landed.
          window.location.href = theme.routes.cart;
        }
      })
      .catch(function (error) {
        setBusy(false);
        showError(error);
      });
  }

  function shouldOpenDrawer() {
    var afterAtc = theme.settings.afterAtc;
    if (afterAtc === 'drawer') return true;
    if (afterAtc === 'drawer_desktop') {
      return !!(theme.mediaMatches && theme.mediaMatches.md && theme.mediaMatches.md.matches);
    }
    return false;
  }

  function setBusy(state) {
    busy = state;
    if (!addButton) return;
    if (state) {
      addButton.setAttribute('aria-disabled', 'true');
      addButton.classList.add('is-loading');
    } else {
      addButton.removeAttribute('aria-disabled');
      addButton.classList.remove('is-loading');
    }
  }

  function showError(error) {
    if (!errorEl) return;
    errorEl.textContent =
      'Could not add the crate and lid together. Please try again, or add the lid separately.';
    errorEl.hidden = false;
    if (window.console && console.warn) console.warn('[aykasa-lid-addon]', error);
  }

  function clearError() {
    if (!errorEl) return;
    errorEl.hidden = true;
    errorEl.textContent = '';
  }

  // The shop's own money format, minus the currency suffix: "$15.90 SGD" twice
  // in a compact row reads badly.
  function money(cents) {
    var format =
      (window.theme && theme.settings && theme.settings.moneyWithCurrencyFormat) ||
      '${{amount}}';
    var amount = withThousands((cents / 100).toFixed(2));
    var text = format
      .replace(/\{\{\s*amount\s*\}\}/, amount)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, withThousands(String(Math.round(cents / 100))));
    if (text.indexOf('{{') !== -1) text = '$' + amount;
    return text
      .replace(/<[^>]*>/g, '')
      .replace(/\s*[A-Z]{3}\s*$/, '')
      .trim();
  }

  function withThousands(value) {
    var parts = value.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return parts.join('.');
  }
})();
